'use client'

import {
    currencyFormat,
    formatCentsToDisplay,
    parseCurrencyToCents,
} from '@/lib/form-state/money'
import {
    COMMON_COST_SUGGESTIONS,
    costPerService,
    ServiceCostItem,
    totalMonthlyCost,
} from '@/lib/pricing/service-cost'
import { useState } from 'react'
import { MdAdd, MdCalculate, MdClose } from 'react-icons/md'

interface Props {
    /** Rótulo do que se repete no mês: "consultas", "exames", "atendimentos". */
    volumeLabel?: string
    onClose: () => void
    /** Recebe o custo unitário em CENTAVOS. */
    onApply: (costInCents: number) => void
}

/**
 * Calculadora do custo de um atendimento a partir das despesas do mês.
 *
 * Existe porque "preço de custo" é o campo que mais fica em branco no catálogo: o
 * operador sabe quanto gasta por mês e quantos atendimentos faz, mas não tem o
 * número por atendimento na cabeça — e sem custo não há markup nem margem para
 * conferir depois.
 *
 * O estado é local e descartável: quem confirma leva só o resultado. Guardar a
 * lista exigiria decidir onde ela vive (por item? por filial? por profissional?),
 * e a conta é a mesma feita de novo em dois minutos.
 */
export function CostCalculatorModal({
    volumeLabel = 'atendimentos',
    onClose,
    onApply,
}: Props) {
    const [costs, setCosts] = useState<ServiceCostItem[]>([])
    const [monthlyVolume, setMonthlyVolume] = useState(0)

    const monthly = totalMonthlyCost(costs)
    const unit = costPerService(costs, monthlyVolume)

    const add = (description = '') => {
        setCosts(current => [...current, { description, amount: 0 }])
    }

    const remove = (index: number) => {
        setCosts(current => current.filter((_, i) => i !== index))
    }

    const setField = (index: number, field: keyof ServiceCostItem, raw: string) => {
        setCosts(current =>
            current.map((cost, i) => {
                if (i !== index) return cost
                if (field === 'description') return { ...cost, description: raw }
                return { ...cost, amount: parseCurrencyToCents(currencyFormat(raw, true)) }
            })
        )
    }

    const usedDescriptions = costs.map(cost => cost.description.trim().toLowerCase())

    return (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-base-100 rounded-lg shadow-2xl w-full !max-w-2xl max-h-[92vh] flex flex-col">
                <header className="flex items-start justify-between gap-3 border-b border-base-300/60 px-6 py-4">
                    <div className="min-w-0">
                        <h3 className="font-bold text-lg flex items-center gap-2">
                            <MdCalculate className="text-secondary" />
                            Calcular o custo
                        </h3>
                        <p className="text-sm text-base-content/60">
                            Liste o que você gasta por mês e quantos {volumeLabel} faz —
                            o custo de um sai do rateio.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <MdClose />
                    </button>
                </header>

                <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden px-6 py-5 flex flex-col gap-4">
                    <div className="flex flex-wrap gap-2">
                        {COMMON_COST_SUGGESTIONS.filter(
                            suggestion => !usedDescriptions.includes(suggestion.toLowerCase())
                        ).map(suggestion => (
                            <button
                                key={suggestion}
                                type="button"
                                className="btn btn-xs btn-outline"
                                onClick={() => add(suggestion)}
                            >
                                <MdAdd className="text-sm" />
                                {suggestion}
                            </button>
                        ))}
                    </div>

                    <ul className="flex flex-col gap-3">
                        {costs.map((cost, index) => (
                            <li key={index} className="flex items-end gap-3">
                                <div className="form-control flex-1 min-w-0">
                                    <label className="label pb-1" htmlFor={`cost-${index}`}>
                                        <span className="label-text font-medium">Custo</span>
                                    </label>
                                    <input
                                        id={`cost-${index}`}
                                        className="input input-bordered w-full"
                                        value={cost.description}
                                        onChange={e =>
                                            setField(index, 'description', e.target.value)
                                        }
                                        placeholder="Aluguel da sala"
                                    />
                                </div>

                                <div className="form-control w-40">
                                    <label
                                        className="label pb-1"
                                        htmlFor={`cost-amount-${index}`}
                                    >
                                        <span className="label-text font-medium">
                                            Valor/mês
                                        </span>
                                    </label>
                                    <input
                                        id={`cost-amount-${index}`}
                                        className="input input-bordered w-full"
                                        value={formatCentsToDisplay(cost.amount)}
                                        onChange={e => setField(index, 'amount', e.target.value)}
                                        placeholder="R$ 0,00"
                                        inputMode="numeric"
                                    />
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-ghost btn-circle mb-1"
                                    onClick={() => remove(index)}
                                    aria-label="Remover custo"
                                >
                                    <MdClose className="text-lg" />
                                </button>
                            </li>
                        ))}
                    </ul>

                    <button
                        type="button"
                        className="btn btn-outline btn-sm gap-1 self-start"
                        onClick={() => add()}
                    >
                        <MdAdd className="text-lg" />
                        Adicionar custo
                    </button>

                    <div className="rounded-lg border border-base-300 p-4 flex flex-col gap-4">
                        <div className="form-control w-full sm:max-w-xs">
                            <label className="label pb-1" htmlFor="monthly-volume">
                                <span className="label-text font-medium">
                                    Quantos {volumeLabel} por mês?
                                </span>
                            </label>
                            <input
                                id="monthly-volume"
                                className="input input-bordered w-full"
                                value={monthlyVolume || ''}
                                onChange={e => {
                                    const parsed = parseInt(
                                        e.target.value.replace(/\D/g, ''),
                                        10
                                    )
                                    setMonthlyVolume(Number.isFinite(parsed) ? parsed : 0)
                                }}
                                placeholder="80"
                                inputMode="numeric"
                            />
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="min-w-0">
                                <p className="text-sm text-base-content/60">Custo mensal</p>
                                <p className="text-lg font-semibold text-base-content">
                                    {formatCentsToDisplay(monthly) || 'R$ 0,00'}
                                </p>
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm text-base-content/60">
                                    Custo por atendimento
                                </p>
                                <p className="text-xl font-semibold text-base-content">
                                    {formatCentsToDisplay(unit) || 'R$ 0,00'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {monthly > 0 && monthlyVolume <= 0 && (
                        <div className="alert alert-warning text-sm">
                            <span className="whitespace-normal">
                                Informe quantos {volumeLabel} você faz por mês. Sem isso não
                                dá para saber quanto custa um — e o custo do mês inteiro
                                viraria o custo de cada atendimento.
                            </span>
                        </div>
                    )}
                </div>

                <footer className="border-t border-base-300/60 px-6 py-4 flex items-center justify-end gap-2">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary gap-2"
                        onClick={() => {
                            onApply(unit)
                            onClose()
                        }}
                        disabled={unit <= 0}
                        title={unit <= 0 ? 'Preencha os custos e o volume do mês' : undefined}
                    >
                        <MdCalculate className="text-lg" />
                        Usar {formatCentsToDisplay(unit) || 'este valor'}
                    </button>
                </footer>
            </div>
        </div>
    )
}
